import React from "react";
import { EMPTY, pct1, pyRound, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { ArchetypeDot } from "../LegendItem";
import { CHART } from "./chartTokens";

// One share on a track with the peer value as a tick (the team's Roster Mix
// vs Peers: an archetype's share of the squad's minutes in bm-orange, the
// peer average as an ink tick, the signed difference at the end in
// bm-positive or bm-critical). Both numbers are printed under the track
// (no hover-only numbers), and the aria-label says the row in words.
//
//   ShareTrack({ label, swatch, value, peer, diff, scale = 40, format, valueText, phone })
//
// swatch     the archetype's data colour (chartTokens.archetypeColor), drawn
//            with a 1px ring; omit for no dot
// value      the squad's share (null: an empty track); peer the peer value
//            (null: no tick, the difference reads "–")
// diff       the signed difference (default value - peer), printed whole
// scale      the value that fills the track (the mock uses max(40, largest))
// valueText  the line under the track (default "13.0% · peers 14.7%"; a
//            missing value reads "–", never "–%")

const cx = (...parts) => parts.filter(Boolean).join(" ");

const pctOf = (v, scale) => {
  const n = toNum(v);
  if (n === null || !scale) return 0;
  return Math.max(0, Math.min(100, (n / scale) * 100));
};

export const signedWhole = (v) => {
  const n = toNum(v);
  if (n === null) return EMPTY;
  const r = pyRound(n);
  if (r === 0) return "0";
  return `${r > 0 ? "+" : "−"}${Math.abs(r)}`;
};

export const ShareTrack = ({ label, swatch, value, peer, diff, scale = 40, format = pct1, valueText, phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  const mine = toNum(value);
  const theirs = toNum(peer);
  const delta = diff !== undefined ? toNum(diff) : mine !== null && theirs !== null ? mine - theirs : null;
  const text = valueText || `${format(mine)} · peers ${format(theirs)}`;
  const summary = `${label}: ${format(mine)}, peers ${format(theirs)}, difference ${signedWhole(delta)}`;
  return (
    <div className={cx("grid items-center gap-[10px]", phone ? "grid-cols-[104px_minmax(0,1fr)_40px]" : "grid-cols-[136px_minmax(0,1fr)_52px]")}>
      <span className="flex min-w-0 items-center gap-[7px]">
        {swatch && <ArchetypeDot color={swatch} size={9} />}
        <span className="truncate bm-label-m text-bm-ink">{label}</span>
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span role="img" aria-label={summary} className="relative block h-[14px] rounded-[3px] bg-bm-mutedbg">
          <span aria-hidden="true" className="absolute inset-y-0 left-0 rounded-[3px] bg-bm-orange" style={{ width: `${pctOf(mine, scale)}%` }} />
          {theirs !== null && (
            <span aria-hidden="true" className="absolute -bottom-[3px] -top-[3px] w-[2px] rounded-full" style={{ left: `${pctOf(theirs, scale)}%`, background: CHART.ink, opacity: 0.55 }} />
          )}
        </span>
        <span aria-hidden="true" className="bm-body-xs text-bm-muted">
          {text}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={cx(
          "text-right font-archivo text-[11px] font-bold",
          delta === null ? "text-bm-tertiary" : delta >= 0 ? "text-bm-positive" : "text-bm-critical",
        )}
      >
        {signedWhole(delta)}
      </span>
    </div>
  );
};

export default ShareTrack;
